/**
 * Cross-Entity Workflow Orchestration Service
 *
 * Coordinates complex workflows that span multiple entities:
 * - Community loan application flows
 * - Collateral pool management
 * - Governance-bank integration
 * - Financial workflow coordination
 */

import {
  CommunityLoanProposal,
  ResidentLoanApplication,
  CollateralAsset,
  CollateralPoolSummary,
  LoanRiskAssessment,
  BankDecisionSummary,
  BankProposalResponse,
} from '@/types/entities/bank';

// ====== WORKFLOW TYPES ======

export interface CommunityLoanWorkflow {
  workflowId: string;
  communityId: string;
  initiatedBy: string; // resident ID
  currentStage: WorkflowStage;
  stages: WorkflowStageStatus[];
  applicationData: CommunityLoanProposal;
  timeline: WorkflowEvent[];
  metadata: WorkflowMetadata;
}

export type WorkflowStage =
  | 'resident_application'
  | 'community_review'
  | 'governance_approval'
  | 'bank_submission'
  | 'bank_review'
  | 'bank_decision'
  | 'loan_activation'
  | 'complete';

export interface WorkflowStageStatus {
  stage: WorkflowStage;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped';
  startedAt?: Date;
  completedAt?: Date;
  assignedTo?: string;
  metadata?: Record<string, unknown>;
}

export interface WorkflowEvent {
  eventId: string;
  timestamp: Date;
  stage: WorkflowStage;
  eventType:
    'stage_started' | 'stage_completed' | 'decision_made' | 'notification_sent' | 'error_occurred';
  description: string;
  actor: string; // user ID who triggered the event
  entityType: 'resident' | 'community' | 'governance' | 'bank' | 'system';
  metadata?: Record<string, unknown>;
}

export interface WorkflowMetadata {
  priority: 'low' | 'normal' | 'high' | 'urgent';
  estimatedDuration: number; // days
  stakeholders: WorkflowStakeholder[];
  riskLevel: 'low' | 'medium' | 'high';
  complianceRequirements: string[];
}

export interface WorkflowStakeholder {
  userId: string;
  role: 'applicant' | 'community_admin' | 'governance_member' | 'bank_lender' | 'observer';
  permissions: WorkflowPermission[];
  notificationPreferences: NotificationPreference[];
}

export type WorkflowPermission =
  | 'view_application'
  | 'edit_application'
  | 'approve_application'
  | 'reject_application'
  | 'view_bank_decision'
  | 'comment_on_proposal'
  | 'receive_notifications';

export interface NotificationPreference {
  channel: 'email' | 'in_app' | 'sms';
  events: WorkflowEventType[];
  frequency: 'immediate' | 'daily_digest' | 'weekly_digest';
}

export type WorkflowEventType =
  | 'stage_transition'
  | 'approval_required'
  | 'decision_made'
  | 'deadline_approaching'
  | 'workflow_completed'
  | 'error_occurred';

// ====== WORKFLOW ORCHESTRATION SERVICE ======

class WorkflowOrchestrationService {
  /**
   * Initiate a new community loan workflow
   */
  async initiateLoanWorkflow(
    communityId: string,
    applicantId: string,
    applicationData: CommunityLoanProposal,
  ): Promise<CommunityLoanWorkflow> {
    const workflowId = `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const workflow: CommunityLoanWorkflow = {
      workflowId,
      communityId,
      initiatedBy: applicantId,
      currentStage: 'resident_application',
      stages: this.initializeStages(),
      applicationData,
      timeline: [
        {
          eventId: `event_${Date.now()}`,
          timestamp: new Date(),
          stage: 'resident_application',
          eventType: 'stage_started',
          description: 'Community loan application workflow initiated',
          actor: applicantId,
          entityType: 'resident',
        },
      ],
      metadata: {
        priority: this.calculatePriority(applicationData),
        estimatedDuration: this.estimateWorkflowDuration(applicationData),
        stakeholders: await this.identifyStakeholders(communityId, applicantId),
        riskLevel: this.assessWorkflowRisk(applicationData),
        complianceRequirements: this.getComplianceRequirements(applicationData),
      },
    };

    // Store workflow (in real implementation, this would go to database)
    await this.storeWorkflow(workflow);

    // Send initial notifications
    await this.sendWorkflowNotifications(workflow, 'workflow_initiated');

    return workflow;
  }

  /**
   * Progress workflow to next stage
   */
  async progressWorkflow(
    workflowId: string,
    completedStage: WorkflowStage,
    actorId: string,
    metadata?: Record<string, unknown>,
  ): Promise<CommunityLoanWorkflow> {
    const workflow = await this.getWorkflow(workflowId);

    // Mark current stage as completed
    const stageIndex = workflow.stages.findIndex((s) => s.stage === completedStage);
    if (stageIndex !== -1) {
      workflow.stages[stageIndex].status = 'completed';
      workflow.stages[stageIndex].completedAt = new Date();
      if (metadata) {
        workflow.stages[stageIndex].metadata = metadata;
      }
    }

    // Determine next stage
    const nextStage = this.getNextStage(completedStage);
    if (nextStage) {
      workflow.currentStage = nextStage;

      // Mark next stage as in progress
      const nextStageIndex = workflow.stages.findIndex((s) => s.stage === nextStage);
      if (nextStageIndex !== -1) {
        workflow.stages[nextStageIndex].status = 'in_progress';
        workflow.stages[nextStageIndex].startedAt = new Date();
      }
    }

    // Add timeline event
    workflow.timeline.push({
      eventId: `event_${Date.now()}`,
      timestamp: new Date(),
      stage: completedStage,
      eventType: 'stage_completed',
      description: `Stage ${completedStage} completed, progressing to ${nextStage}`,
      actor: actorId,
      entityType: this.getEntityTypeForStage(completedStage),
      metadata,
    });

    // Update workflow
    await this.updateWorkflow(workflow);

    // Send notifications for stage transition
    await this.sendWorkflowNotifications(workflow, 'stage_transition');

    return workflow;
  }

  /**
   * Handle bank decision integration
   */
  async handleBankDecision(
    workflowId: string,
    bankDecision: BankDecisionSummary,
    bankLenderId: string,
  ): Promise<CommunityLoanWorkflow> {
    const workflow = await this.getWorkflow(workflowId);

    // Update workflow with bank decision
    workflow.timeline.push({
      eventId: `event_${Date.now()}`,
      timestamp: new Date(),
      stage: 'bank_decision',
      eventType: 'decision_made',
      description: `Bank decision: ${bankDecision.decision}`,
      actor: bankLenderId,
      entityType: 'bank',
      metadata: { bankDecision },
    });

    if (bankDecision.decision?.decision === 'approved') {
      await this.progressWorkflow(workflowId, 'bank_decision', bankLenderId, { bankDecision });
    } else {
      // Handle rejection
      workflow.currentStage = 'complete';
      const stageIndex = workflow.stages.findIndex((s) => s.stage === 'bank_decision');
      if (stageIndex !== -1) {
        workflow.stages[stageIndex].status = 'failed';
        workflow.stages[stageIndex].metadata = { bankDecision };
      }
    }

    await this.updateWorkflow(workflow);
    await this.sendWorkflowNotifications(workflow, 'decision_made');

    return workflow;
  }

  /**
   * Get workflow by ID
   */
  async getWorkflow(workflowId: string): Promise<CommunityLoanWorkflow> {
    // Mock implementation - in real app, this would fetch from database
    const mockWorkflow: CommunityLoanWorkflow = {
      workflowId,
      communityId: 'community-1',
      initiatedBy: 'resident-1',
      currentStage: 'resident_application',
      stages: this.initializeStages(),
      applicationData: {
        id: 'temp-proposal',
        communityId: 'community-1',
        communityName: 'Test Community',
        projectTitle: 'Infrastructure Development',
        projectDescription: 'Community infrastructure project',
        projectType: 'infrastructure',
        totalLoanAmount: 300000,
        individualLoanAmount: 600,
        participantCount: 500,
        loanTerm: 240,
        collateralPool: {} as unknown as CollateralPoolSummary,
        residents: [],
        communityFinancials: {} as unknown as Record<string, unknown>,
        riskAssessment: {} as unknown as LoanRiskAssessment,
        submittedAt: new Date(),
        status: 'submitted',
        processingHistory: [],
      } as unknown as CommunityLoanProposal,
      timeline: [],
      metadata: {
        priority: 'normal',
        estimatedDuration: 30,
        stakeholders: [],
        riskLevel: 'medium',
        complianceRequirements: [],
      },
    };

    return mockWorkflow;
  }

  /**
   * Initialize workflow stages
   */
  private initializeStages(): WorkflowStageStatus[] {
    const stages: WorkflowStage[] = [
      'resident_application',
      'community_review',
      'governance_approval',
      'bank_submission',
      'bank_review',
      'bank_decision',
      'loan_activation',
      'complete',
    ];

    return stages.map((stage) => ({
      stage,
      status: stage === 'resident_application' ? 'in_progress' : 'pending',
      startedAt: stage === 'resident_application' ? new Date() : undefined,
    }));
  }

  /**
   * Get next stage in workflow
   */
  private getNextStage(currentStage: WorkflowStage): WorkflowStage | null {
    const stageOrder: WorkflowStage[] = [
      'resident_application',
      'community_review',
      'governance_approval',
      'bank_submission',
      'bank_review',
      'bank_decision',
      'loan_activation',
      'complete',
    ];

    const currentIndex = stageOrder.indexOf(currentStage);
    return currentIndex < stageOrder.length - 1 ? stageOrder[currentIndex + 1] : null;
  }

  /**
   * Calculate workflow priority
   */
  private calculatePriority(
    applicationData: CommunityLoanProposal,
  ): 'low' | 'normal' | 'high' | 'urgent' {
    if (applicationData.totalLoanAmount > 500000) return 'high';
    if (applicationData.totalLoanAmount > 100000) return 'normal';
    return 'low';
  }

  /**
   * Estimate workflow duration in days
   */
  private estimateWorkflowDuration(applicationData: CommunityLoanProposal): number {
    const baseTime = 14; // 2 weeks baseline
    const complexityMultiplier = applicationData.totalLoanAmount > 500000 ? 1.5 : 1;
    return Math.ceil(baseTime * complexityMultiplier);
  }

  /**
   * Assess workflow risk level
   */
  private assessWorkflowRisk(applicationData: CommunityLoanProposal): 'low' | 'medium' | 'high' {
    if (applicationData.totalLoanAmount > 1000000) return 'high';
    if (applicationData.totalLoanAmount > 250000) return 'medium';
    return 'low';
  }

  /**
   * Get compliance requirements
   */
  private getComplianceRequirements(applicationData: CommunityLoanProposal): string[] {
    const requirements = ['Basic KYC', 'Financial Disclosure'];
    if (applicationData.totalLoanAmount > 250000) {
      requirements.push('Enhanced Due Diligence', 'Regulatory Approval');
    }
    return requirements;
  }

  /**
   * Identify workflow stakeholders
   */
  private async identifyStakeholders(
    communityId: string,
    applicantId: string,
  ): Promise<WorkflowStakeholder[]> {
    // Mock implementation
    return [
      {
        userId: applicantId,
        role: 'applicant',
        permissions: ['view_application', 'edit_application', 'receive_notifications'],
        notificationPreferences: [
          {
            channel: 'email',
            events: ['stage_transition', 'decision_made'],
            frequency: 'immediate',
          },
        ],
      },
    ];
  }

  /**
   * Get entity type for stage
   */
  private getEntityTypeForStage(
    stage: WorkflowStage,
  ): 'resident' | 'community' | 'governance' | 'bank' | 'system' {
    const stageEntityMap: Record<
      WorkflowStage,
      'resident' | 'community' | 'governance' | 'bank' | 'system'
    > = {
      resident_application: 'resident',
      community_review: 'community',
      governance_approval: 'governance',
      bank_submission: 'system',
      bank_review: 'bank',
      bank_decision: 'bank',
      loan_activation: 'system',
      complete: 'system',
    };
    return stageEntityMap[stage];
  }

  /**
   * Store workflow (mock implementation)
   */
  private async storeWorkflow(workflow: CommunityLoanWorkflow): Promise<void> {
    // Mock storage - in real implementation, this would save to database
    console.log('Storing workflow:', workflow.workflowId);
  }

  /**
   * Update workflow (mock implementation)
   */
  private async updateWorkflow(workflow: CommunityLoanWorkflow): Promise<void> {
    // Mock update - in real implementation, this would update database
    console.log('Updating workflow:', workflow.workflowId);
  }

  /**
   * Send workflow notifications (mock implementation)
   */
  private async sendWorkflowNotifications(
    workflow: CommunityLoanWorkflow,
    eventType: string,
  ): Promise<void> {
    // Mock notifications - in real implementation, this would send actual notifications
    console.log('Sending notifications for workflow:', workflow.workflowId, 'event:', eventType);
  }
}

// Export singleton instance
export const workflowOrchestrationService = new WorkflowOrchestrationService();
